import { useState, useCallback } from 'react';
import { message } from 'antd';
import { expandMultiToothRange } from '../components/formTypes/odontogram/odontogramUtils';
import { getSurfaceLabel } from '../components/formTypes/odontogram/ToothDiagram';
import { toothColors, eraserCodes, codeByKey, getOclusalSurfaces } from '../components/formTypes/odontogram/odontogramConfig';

const normalise = (raw) => {
  if (!raw || typeof raw !== 'object') return { toothFindings: {}, multiToothFindings: [], cpodExcludedTeeth: [] };

  if ('toothFindings' in raw) {
    return {
      toothFindings: raw.toothFindings || {},
      multiToothFindings: raw.multiToothFindings || [],
      cpodExcludedTeeth: raw.cpodExcludedTeeth || [],
    };
  }

  return { toothFindings: raw, multiToothFindings: [], cpodExcludedTeeth: [] };
};

const oclusalBulkCodes = new Set(['CARIES', 'RESTAURACION', 'RESTAURACION_TEMPORAL', 'SELLANTE']);

const createFindingEntry = (finding, toothNumber, surface) => ({
  type: finding.code,
  code: finding.code,
  label: finding.label,
  surface,
  surfaceLabel: surface ? getSurfaceLabel(toothNumber, surface) : null,
  color: finding.color,
  conditionMode: finding.colorMode === 'user'
    ? (finding.color === toothColors.pathological ? 'pathological' : 'healthy')
    : (finding.colorMode || 'healthy'),
  subCode: finding.subCode || null,
  createdAt: new Date().toISOString(),
});

const useOdontogram = (initialData = {}) => {
  const [state, setState] = useState(() => normalise(initialData));
  const [activeFinding, setActiveFinding] = useState(null);

  const addFinding = useCallback((toothNumber, surface, finding) => {
    if (!finding) {
      message.warning('Por favor seleccione un hallazgo del menú primero.');
      return;
    }

    setState(previousState => {
      const currentFindings = previousState.toothFindings[toothNumber] || [];

      if (finding.code === eraserCodes.single) {
        if (surface) {
          const index = [...currentFindings].map(findingItem => findingItem.surface).lastIndexOf(surface);
          if (index === -1) return previousState;
          const updated = [...currentFindings];
          updated.splice(index, 1);
          return { ...previousState, toothFindings: { ...previousState.toothFindings, [toothNumber]: updated } };
        }
        const updated = [...currentFindings];
        const lastWhole = [...updated].reverse().findIndex(findingItem => !findingItem.surface);
        if (lastWhole === -1) return previousState;
        updated.splice(updated.length - 1 - lastWhole, 1);
        return { ...previousState, toothFindings: { ...previousState.toothFindings, [toothNumber]: updated } };
      }

      if (finding.code === eraserCodes.multi) {
        return { ...previousState, toothFindings: { ...previousState.toothFindings, [toothNumber]: [] } };
      }

      const isMissing = currentFindings.some(findingItem => findingItem.type === codeByKey.missingTooth);
      if (isMissing && finding.code !== codeByKey.missingTooth && !finding.eraser) {
        message.error(
          `No se puede registrar hallazgos en la pieza ${toothNumber} porque está marcada como AUSENTE.`
        );
        return previousState;
      }

      if (surface?.startsWith('O') && oclusalBulkCodes.has(finding.code)) {
        const allOclusal = getOclusalSurfaces(toothNumber);
        const newEntries = allOclusal
          .filter(surface => !currentFindings.some(findingItem => findingItem.type === finding.code && findingItem.surface === surface))
          .map(surface => createFindingEntry(finding, toothNumber, surface));

        if (newEntries.length === 0) return previousState;
        return {
          ...previousState,
          toothFindings: {
            ...previousState.toothFindings,
            [toothNumber]: [...currentFindings, ...newEntries],
          },
        };
      }

      if (!surface && currentFindings.some(findingItem => findingItem.type === finding.code && !findingItem.surface)) {
        return previousState;
      }

      if (surface && currentFindings.some(findingItem => findingItem.type === finding.code && findingItem.surface === surface)) {
        return previousState;
      }

      return {
        ...previousState,
        toothFindings: {
          ...previousState.toothFindings,
          [toothNumber]: [...currentFindings, createFindingEntry(finding, toothNumber, surface)],
        },
      };
    });
  }, []);

  const addMultiFinding = useCallback((finding, fromTooth, toTooth) => {
    if (!finding || !fromTooth || !toTooth) return;
    setState(prev => ({
      ...prev,
      multiToothFindings: [
        ...prev.multiToothFindings,
        {
          type: finding.code,
          code: finding.code,
          label: finding.label,
          fromTooth,
          toTooth,
          teeth: expandMultiToothRange(fromTooth, toTooth),
          color: finding.color,
          createdAt: new Date().toISOString(),
        },
      ],
    }));
  }, []);

  const removeMultiFinding = useCallback((index) => {
    setState(prev => ({
      ...prev,
      multiToothFindings: prev.multiToothFindings.filter((_, i) => i !== index),
    }));
  }, []);

  const removeFinding = useCallback((toothNumber, findingIndex) => {
    setState(prev => {
      const existing = prev.toothFindings[toothNumber] || [];
      const updated = existing.filter((_, i) => i !== findingIndex);
      const next = { ...prev.toothFindings };
      if (updated.length > 0) {
        next[toothNumber] = updated;
      } else {
        delete next[toothNumber];
      }
      return { ...prev, toothFindings: next };
    });
  }, []);

  const clearAllFindings = useCallback(() => {
    setState(previousState => ({ ...previousState, toothFindings: {}, multiToothFindings: [] }));
  }, []);

  const toggleCpodExclusion = useCallback((toothNumber) => {
    const tooth = Number(toothNumber);
    setState(previousState => {
      const excluded = new Set(previousState.cpodExcludedTeeth || []);
      if (excluded.has(tooth)) excluded.delete(tooth);
      else excluded.add(tooth);
      return { ...previousState, cpodExcludedTeeth: [...excluded] };
    });
  }, []);

  const selectFinding = useCallback((finding) => {
    setActiveFinding(finding);
  }, []);

  const setOdontogramData = useCallback((raw) => {
    setState(normalise(raw));
  }, []);

  return {
    toothFindings: state.toothFindings,
    multiToothFindings: state.multiToothFindings,
    cpodExcludedTeeth: state.cpodExcludedTeeth,
    activeFinding,
    selectFinding,
    addFinding,
    addMultiFinding,
    removeMultiFinding,
    removeFinding,
    clearAllFindings,
    toggleCpodExclusion,
    setOdontogramData,
  };
};

export default useOdontogram;
